import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getMergedCompetencies } from 'lib/competencies';
import { isAiConfigAvailableForUser } from 'shared-helpers/aiTools';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import { FlexContainer, MoreOptions } from 'components/common';
import { Box, BoxEmpty, BoxRow } from 'components/common/box';
import {
  ProfileCompetenciesAdd,
  ProfileCompetenciesBadge,
  ProfileCompetenciesCompare,
  ProfileCompetencyMapping,
  ProfileCompetenciesEndorsementsActions
} from '.';

import type { RequestUser } from 'shared-types';
import type {
  GetCompetencyProfilesRes,
  GetUsersRes
} from 'shared-types/responses';

interface Props {
  apiUrl: string;
  isPreview?: boolean;
  isUserPage: boolean;
  targetUser: RequestUser;
}

const ProfileCompetencies = ({
  apiUrl,
  isPreview,
  isUserPage,
  targetUser
}: Props) => {
  const { messages, serverConfig, user: loggedInUser } = useReduxState();

  const { data: competencyProfilesData } = useApiData<GetCompetencyProfilesRes>(
    {
      url: '/admin/competency-profiles?limit=-1'
    }
  );

  const competencyProfiles = competencyProfilesData?.competencyProfiles || [];

  const { data: userData, updateData: updateCompetencies } =
    useApiData<GetUsersRes>({
      url: apiUrl + `?id=${targetUser?.id}`,
      onlyIf: Boolean(targetUser?.id)
    });

  const targetUserCompetencies = userData?.users?.[0]?.competencies || [];

  const loggedInUserHasEditPermissions =
    loggedInUser?.roles.includes('superadmin') ||
    (loggedInUser?.roles.includes('admin') &&
      loggedInUser?.permissions.includes('manage-users'));

  const mergedCompetencies = getMergedCompetencies(targetUserCompetencies);

  // const competencyMappingEnabled = isAiConfigAvailableForUser({
  //   requiredAiExtraServerConfig: 'aiExtraCompetencyMapping',
  //   requireAiToolsPermission: false,
  //   serverConfig,
  //   user: loggedInUser
  // });

  const endorsementsEnabled =
    serverConfig.userEnableOtherProfiles?.enableCompetencyEndorsements &&
    attributesConditionsMatch({
      conditions: serverConfig.userEnableOtherProfiles.conditions,
      customAttributeValues: loggedInUser?.customAttributes,
      user: loggedInUser
    }) &&
    attributesConditionsMatch({
      conditions: serverConfig.userEnableOtherProfiles.conditions,
      customAttributeValues: targetUser?.customAttributes,
      user: targetUser
    });

  return (
    <Box
      headerContent={
        !isPreview ? (
          <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
            {/* {competencyMappingEnabled ? <ProfileCompetencyMapping /> : null} */}

            <ProfileCompetenciesAdd
              allowOnlyUserEditableCompetencies={
                !loggedInUserHasEditPermissions
              }
              excludedCompetencies={targetUserCompetencies}
              targetUserId={targetUser.id}
              updateCompetencies={updateCompetencies}
            />

            {competencyProfiles.length ? (
              <MoreOptions category="lighter">
                <ProfileCompetenciesCompare
                  competencyProfiles={competencyProfiles}
                  targetUser={targetUser}
                  targetUserCompetencies={targetUserCompetencies}
                />
              </MoreOptions>
            ) : null}
          </FlexContainer>
        ) : null
      }
      headerContentWrap={true}
      padding={0}
      title={messages.analyticsCompetencies}
    >
      {mergedCompetencies.length ? (
        endorsementsEnabled ? (
          <div className="competencies-with-endorsements">
            {mergedCompetencies.map((competency) => (
              <BoxRow key={competency.id}>
                <div className="competency-row">
                  <div className="competency">
                    <ProfileCompetenciesBadge
                      key={competency.id}
                      competency={competency}
                      enableAdminLinks={!isPreview && isUserPage}
                      showEndorsements={true}
                      showLanguageVersionGrantorTitle={!isUserPage}
                      targetUserId={targetUser?.id}
                      updateCompetencies={
                        isPreview ? undefined : updateCompetencies
                      }
                    />
                  </div>

                  <div className="endorsements">
                    <ProfileCompetenciesEndorsementsActions
                      key={competency.id}
                      competency={competency}
                      targetUserId={targetUser.id}
                      updateCompetencies={updateCompetencies}
                    />
                  </div>
                </div>
              </BoxRow>
            ))}
          </div>
        ) : (
          <div className="competencies">
            {mergedCompetencies.map((competency) => (
              <ProfileCompetenciesBadge
                key={competency.id}
                competency={competency}
                enableAdminLinks={!isPreview && isUserPage}
                showLanguageVersionGrantorTitle={!isUserPage}
                targetUserId={targetUser?.id}
                updateCompetencies={isPreview ? undefined : updateCompetencies}
              />
            ))}
          </div>
        )
      ) : (
        <BoxEmpty align="center" text={messages.commonNoCompetencies} />
      )}

      <style jsx>{`
        .competencies {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          padding: 15px;
        }
        .competencies-with-endorsements .competency-row {
          align-items: flex-start;
          display: flex;
          flex-direction: column;
          gap: 15px 25px;
          justify-content: space-between;
        }
        .competencies-with-endorsements .competency {
          flex-grow: 1;
          flex-shrink: 1;
          max-width: 100%;
          min-width: 0;
        }
        .competencies-with-endorsements .endorsements {
          flex-grow: 0;
          flex-shrink: 0;
          margin-left: auto;
        }
        @media (min-width: ${breakpoints.small}px) {
          .competencies-with-endorsements .competency-row {
            align-items: center;
            flex-direction: row;
          }
        }
      `}</style>
    </Box>
  );
};

export default ProfileCompetencies;
